import { CREATE_SPACE_DIALOG, useWebItemLocation } from '@confluence/web-item-location';

export const useSpaceTemplates = () => {
	const { loading, webItems } = useWebItemLocation({
		location: CREATE_SPACE_DIALOG,
	});

	// Builds a Set of enabled templates based on whether the template is included in the list of web items.
	const enabledTemplates = webItems.reduce(
		(set, webItem) => set.add(TEMPLATE_KEYS[completeBlueprintKey(webItem)]),
		new Set<SpaceTemplate>(),
	);

	return {
		loading,
		enabledTemplates,
	};
};

export type SpaceTemplate =
	| 'blank'
	| 'team'
	| 'documentation'
	| 'knowledge'
	| 'software-legacy'
	| 'software'
	| 'personal'
	| 'marketing-team'
	| 'design-team'
	| 'operations-team'
	| 'project-management-team'
	| 'finance-team'
	| 'customer-support-team'
	| 'legal-team'
	| 'sales-team'
	| 'it-team'
	| 'research-team'
	| 'hr-team'
	| 'product-management-team'
	| 'project-collaboration'
	| 'knowledge-base';

// A "complete blueprint key" is similar to a WebItem complete key,
// except the module key is replaced with a blueprint key.
// That is, it follows the format `<plugin namespace>:<blueprint key>`.
// The create space API is expecting a complete blueprint key to be provided.
export const completeBlueprintKey = ({ completeKey, moduleKey, params }) =>
	completeKey.replace(moduleKey, params?.blueprintKey);

// Onboarding searches for this key specifically to check if a personal space exists
export const PERSONAL_SPACE_TEMPLATE_KEY =
	'com.atlassian.confluence.plugins.confluence-create-content-plugin:create-personal-space-blueprint';

// This is a mapping between "complete blueprint key" (see above) and template.
export const TEMPLATE_KEYS: Record<string, SpaceTemplate> = {
	'com.atlassian.confluence.plugins.confluence-create-content-plugin:create-blank-space-blueprint':
		'blank',
	'com.atlassian.confluence.plugins.confluence-space-blueprints:team-space-blueprint': 'team',
	'com.atlassian.confluence.plugins.confluence-space-blueprints:documentation-space-blueprint':
		'documentation',
	// Legacy knowledge base template
	'com.atlassian.confluence.plugins.confluence-knowledge-base:kb-blueprint': 'knowledge',
	'com.atlassian.confluence.plugins.confluence-space-blueprints:software-project-space-blueprint':
		'software',
	// Legacy software space template. Should be removed when feature is rolled out to 100%
	'com.atlassian.confluence.plugins.confluence-software-project:sp-space-blueprint':
		'software-legacy',
	[PERSONAL_SPACE_TEMPLATE_KEY]: 'personal',
	'com.atlassian.confluence.plugins.confluence-space-blueprints:marketing-team-space-blueprint':
		'marketing-team',
	'com.atlassian.confluence.plugins.confluence-space-blueprints:design-team-space-blueprint':
		'design-team',
	'com.atlassian.confluence.plugins.confluence-space-blueprints:operations-team-space-blueprint':
		'operations-team',
	'com.atlassian.confluence.plugins.confluence-space-blueprints:project-management-team-space-blueprint':
		'project-management-team',
	'com.atlassian.confluence.plugins.confluence-space-blueprints:finance-team-space-blueprint':
		'finance-team',
	'com.atlassian.confluence.plugins.confluence-space-blueprints:customer-support-team-space-blueprint':
		'customer-support-team',
	'com.atlassian.confluence.plugins.confluence-space-blueprints:legal-team-space-blueprint':
		'legal-team',
	'com.atlassian.confluence.plugins.confluence-space-blueprints:sales-team-space-blueprint':
		'sales-team',
	'com.atlassian.confluence.plugins.confluence-space-blueprints:it-team-space-blueprint': 'it-team',
	'com.atlassian.confluence.plugins.confluence-space-blueprints:research-team-space-blueprint':
		'research-team',
	'com.atlassian.confluence.plugins.confluence-space-blueprints:hr-team-space-blueprint': 'hr-team',
	'com.atlassian.confluence.plugins.confluence-space-blueprints:product-management-team-space-blueprint':
		'product-management-team',
	'com.atlassian.confluence.plugins.confluence-space-blueprints:project-collaboration-space-blueprint':
		'project-collaboration',
	'com.atlassian.confluence.plugins.confluence-space-blueprints:knowledge-base-space-blueprint':
		'knowledge-base',
};
